// Pure, Three.js-free. Computes what should be visible at a station and how two
// stations differ. The scene consumes these results to drive geometry + tweens.

import {
  AssemblyGraph,
  StationId,
  StationDef,
  StatusOverlay,
  STATION_ORDER,
  stationIndex,
} from "./types";

export interface RenderState {
  station: StationId;
  /** All occurrence IDs that should be present/visible at this station. */
  visible: Set<string>;
  /** Occurrence IDs introduced *at* this station (rendered highlighted). */
  newThisStation: Set<string>;
  overlay?: StatusOverlay;
}

export interface StateDiff {
  /** In next, not in prev — tween in from installOffset. */
  entering: string[];
  /** In prev, not in next — tween back out (backward nav). */
  leaving: string[];
  /** In both — stay placed; may change highlight. */
  persisting: string[];
}

/**
 * Which stations contribute geometry when the current station is `station`.
 *
 * Two parallel branches (piston_sub: SA*, main: MA*) that converge at MA2.
 * The rule is a union over "stations <= current within the relevant set", so
 * forward / backward / jump navigation all yield the correct set — order-free.
 */
function contributingStations(
  graph: AssemblyGraph,
  station: StationId
): StationId[] {
  const def = graph.stations[station];
  const idx = stationIndex(station);
  const saAll: StationId[] = ["SA1", "SA2", "SA3"];

  switch (def.branch) {
    case "piston_sub":
      // Only the SA stations up to and including the current one.
      return saAll.filter((s) => stationIndex(s) <= idx);

    case "main":
      if (station === "MA1") {
        // Bottom-end + the piston sub-assembly already built in SA (it stays put;
        // the block/crank appear around it). MA2 then adds the remaining pistons.
        return ["MA1", ...saAll];
      }
      // MA2 = the marriage: bottom-end + built sub-assembly + the rest of the pistons.
      return ["MA1", "MA2", ...saAll];

    case "qc":
      // No geometry added; the full assembly is present.
      return ["SA1", "SA2", "SA3", "MA1", "MA2"];
  }
}

export function computeRenderState(
  graph: AssemblyGraph,
  station: StationId
): RenderState {
  const stations = contributingStations(graph, station);
  const visible = new Set<string>();
  for (const s of stations) {
    for (const occ of graph.stations[s].addsOccurrences) visible.add(occ);
  }

  // Only occurrences that actually exist in the graph are addressable.
  for (const id of [...visible]) {
    if (!graph.occurrences[id]) {
      // eslint-disable-next-line no-console
      console.warn(`[assembly-graph] station ${station} references unknown occurrence "${id}"`);
      visible.delete(id);
    }
  }

  // Preview-only occurrences (e.g. the crank throw stand-in) live only in the
  // sub-assembly view; drop them once main/QC assembly begins so they don't overlap
  // the real part (the full crankshaft) added there.
  if (graph.stations[station].branch !== "piston_sub") {
    for (const id of [...visible]) {
      if (graph.occurrences[id]?.previewOnly) visible.delete(id);
    }
  }

  const newThisStation = new Set(
    graph.stations[station].addsOccurrences.filter((id) => visible.has(id))
  );

  return {
    station,
    visible,
    newThisStation,
    overlay: graph.stations[station].statusOverlay,
  };
}

export function diffStates(
  prev: RenderState | null,
  next: RenderState
): StateDiff {
  const prevVisible = prev?.visible ?? new Set<string>();
  const entering: string[] = [];
  const leaving: string[] = [];
  const persisting: string[] = [];

  for (const id of next.visible) {
    if (prevVisible.has(id)) persisting.push(id);
    else entering.push(id);
  }
  for (const id of prevVisible) {
    if (!next.visible.has(id)) leaving.push(id);
  }

  return { entering, leaving, persisting };
}

/** Guard used by callers to sanity-check a graph covers the full vocabulary. */
export function assertGraphStations(graph: AssemblyGraph): void {
  for (const s of STATION_ORDER) {
    if (!graph.stations[s]) {
      throw new Error(`[assembly-graph] missing station definition for "${s}"`);
    }
  }
}

export function isNewLabel(def: StationDef): boolean {
  return def.addsOccurrences.length > 0;
}
